import { DueDateValue } from "@smartsuite/types";
import { FieldRegistryConfig } from "../../types";
import { DateRangeFieldControl } from "./DateRangeFieldControl/DateRangeFieldControl";
import { defaultDateRangeValue, prefillDateRangeBoundary } from "./date-range.helpers";
import { dateRangeToReviewString } from "../review/reviewString.helpers";

interface DateValue {
  date: string | null;
  include_time: boolean;
}

const convertDateValue = (value: DateValue): DateValue => {
  if (!value.date) {
    return { ...value, date: "" };
  }

  if (!value.include_time) {
    // A date-only boundary is a calendar day; pin it to UTC midnight verbatim
    // (no Date parsing) so the submitted day never shifts with the user's timezone.
    const day = value.date.includes("T") ? value.date.slice(0, 10) : value.date;
    return { ...value, date: `${day}T00:00:00.000Z` };
  }

  return { ...value, date: new Date(value.date).toISOString() };
};

export const dateRangeFieldConfig: FieldRegistryConfig<DueDateValue | null> = {
  icon: "date-range",
  control: (props) => <DateRangeFieldControl {...props} />,
  convertFormValueToApi: (value) => {
    // The picker emits null when closed without a selection; submit an empty range.
    const range = value ?? defaultDateRangeValue;

    return {
      from_date: convertDateValue(range.from_date),
      to_date: convertDateValue(range.to_date),
    };
  },
  prefill: ({ value }) => {
    if (typeof value !== "string" || !value || value === ",") {
      return null;
    }

    // A single value (no comma) fills both boundaries so it renders and edits as a
    // range from=to, not a half-empty range showing only an end date.
    if (!value.includes(",")) {
      const boundary = prefillDateRangeBoundary(value);
      if (!boundary) return null;

      return {
        from_date: boundary,
        to_date: boundary,
      };
    }

    // An unparseable boundary falls back to the empty boundary — never a null
    // boundary, which would crash pinDateOnlyToUtcMidnight on render.
    const [fromDate, toDate] = value.split(",");

    return {
      from_date: prefillDateRangeBoundary(fromDate) ?? defaultDateRangeValue.from_date,
      to_date: prefillDateRangeBoundary(toDate) ?? defaultDateRangeValue.to_date,
    };
  },
  toReviewString: (value, _appField, preferences) =>
    dateRangeToReviewString(value, preferences?.locale.time_format, preferences?.timezone?.value),
};
